import {Injectable, isDevMode} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {environment} from "../../../environments/environment";
import axios from "axios";
import {firstValueFrom, Observable} from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class RazorpayService {
    response: any[] = []
env = environment

    constructor(
        private http: HttpClient,

    ) {

    }
    getPayments(){
    return this.http.get(environment.cloudFunctions.fetchPaymentByDate);

    }
    getTransactionsByDateRange(startDate: string, endDate: string): Observable<any[]> {
        const url = `${this.env.cloudFunctions.fetchPaymentByDate}?startDate=${startDate}&endDate=${endDate}`;
        return this.http.get<any[]>(url);
    }

    /**
     * Every payment created between the start of `start` and the end of `end` (local time), newest first.
     *
     * The cloud function has two limits this works around:
     *  - it returns at most PAGE_LIMIT payments per call and has no paging, so the range is fetched
     *    in small windows, and a window that comes back full is split in half and fetched again;
     *  - it reads both dates as midnight UTC and stops before the end date, so the range is padded
     *    by a day on each side and trimmed back to the exact local days here.
     *
     * `truncatedDays` lists any single day that alone has PAGE_LIMIT or more payments (cannot be split further).
     */
    async getAllTransactionsByDateRange(start: Date, end: Date): Promise<{ items: any[], truncatedDays: string[] }> {
        const from = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const to = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);
        const payments = new Map<string, any>();
        const truncatedDays: string[] = [];

        const fetchWindow = async (a: Date, b: Date): Promise<void> => {
            const res: any = await firstValueFrom(this.getTransactionsByDateRange(this.toDateParam(a), this.toDateParam(b)));
            const items: any[] = res?.items || [];
            const days = Math.round((b.getTime() - a.getTime()) / this.DAY_MS);
            if (items.length >= this.PAGE_LIMIT && days > 1) {
                const mid = this.addDays(a, Math.floor(days / 2));
                await fetchWindow(a, mid);
                await fetchWindow(mid, b);
                return;
            }
            if (items.length >= this.PAGE_LIMIT) truncatedDays.push(this.toDateParam(a));
            items.forEach(item => payments.set(item.id, item));
        };

        // Windows share their boundary day because the function excludes the end date
        const windows: [Date, Date][] = [];
        // No payments exist in the future, so a range running past today is cut short
        const dayAfterEnd = this.addDays(from, Math.round((to.getTime() - from.getTime()) / this.DAY_MS) + 1);
        const dayAfterTomorrow = this.addDays(new Date(), 2);
        const last = dayAfterEnd < dayAfterTomorrow ? dayAfterEnd : dayAfterTomorrow;
        for (let a = this.addDays(from, -1); a < last; a = this.addDays(a, this.WINDOW_DAYS)) {
            const b = this.addDays(a, this.WINDOW_DAYS);
            windows.push([a, b < last ? b : last]);
        }
        for (let i = 0; i < windows.length; i += this.PARALLEL_WINDOWS) {
            await Promise.all(windows.slice(i, i + this.PARALLEL_WINDOWS).map(([a, b]) => fetchWindow(a, b)));
        }

        const items = Array.from(payments.values())
            .filter(item => item.created_at * 1000 >= from.getTime() && item.created_at * 1000 <= to.getTime())
            .sort((x, y) => y.created_at - x.created_at);
        return {items, truncatedDays};
    }

    private readonly PAGE_LIMIT = 100;
    private readonly WINDOW_DAYS = 7;
    private readonly PARALLEL_WINDOWS = 4;
    private readonly DAY_MS = 24 * 60 * 60 * 1000;

    private addDays(date: Date, days: number): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
    }

    private toDateParam(date: Date): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    }

}
